import React from 'react'
import { Link } from 'react-router-dom'
import { motion, useInView } from 'framer-motion'
import { Heart } from 'lucide-react'

const Footer = () => {
  const ref = React.useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  }

  const itemVariants = {
    hidden: { 
      opacity: 0,
      y: 20,
      filter: 'blur(6px)'
    },
    visible: { 
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      transition: {
        type: 'spring' as const,
        bounce: 0.3,
        duration: 0.8,
      },
    },
  }

  return (
    <footer className="relative border-t bg-background/50 backdrop-blur-sm">
      <motion.div 
        ref={ref}
        variants={containerVariants}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        className="mx-auto max-w-7xl px-6 py-12 md:py-16"
      >
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
          {/* Logo and Description */}
          <motion.div variants={itemVariants} className="lg:col-span-2">
            <div className="flex items-center space-x-2 mb-4">
              <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center text-primary-foreground font-bold text-lg">
                D
              </div>
              <span className="text-xl font-bold font-poppins">DesignStack</span>
            </div>
            <p className="text-muted-foreground text-sm max-w-md font-inter">
              Find the perfect design tools for your workflow. Discover, compare, and choose from the best UX/UI and product design tools available.
            </p>
          </motion.div>

          {/* Quick Links */}
          <motion.div variants={itemVariants}>
            <h3 className="font-semibold mb-4 font-poppins">Quick Links</h3>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to="/unlock" className="text-muted-foreground hover:text-foreground transition-colors font-inter">
                  Get Access
                </Link>
              </li>
              <li>
                <a 
                  href="mailto:paulofhreire@gmail.com" 
                  className="text-muted-foreground hover:text-foreground transition-colors font-inter"
                >
                  Contact
                </a>
              </li>
              <li>
                <a 
                  href="https://freiretdr.com/p/new-page-02d4d7e6" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors font-inter"
                >
                  My Story
                </a>
              </li>
            </ul>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <motion.div 
          variants={itemVariants}
          className="mt-12 pt-8 border-t border-border/50 flex flex-col sm:flex-row justify-between items-center gap-4"
        >
          <p className="text-sm text-muted-foreground font-inter">
            © 2025 DesignStack. Made with <Heart className="inline h-4 w-4 text-red-500" /> for designers.
          </p>
          <div className="flex space-x-6 text-sm">
            <Link to="#" className="text-muted-foreground hover:text-foreground transition-colors font-inter">
              Privacy Policy
            </Link>
            <Link to="#" className="text-muted-foreground hover:text-foreground transition-colors font-inter">
              Terms of Service
            </Link>
          </div>
        </motion.div>
      </motion.div>
    </footer>
  )
}

export { Footer }